import React, { FC, useEffect, useState } from 'react';
import {
  ActiveServiceFile,
  contextCallbacksMockServices,
  DataDefinitionContext,
  PanelFeatures,
} from '@blazemeter/blaze-data-ui';
import _ from 'lodash';
import { assetRepoFetch, dataDesignerFetch, dataDesignerFetchDP } from '../../axios/tdm-http-client';
import { Service, Transaction } from '../../types/rest/assetCatalogTypes';
import { DataModelState } from '../../store/dataModel/dataModelTypes';
import { useDispatch } from 'react-redux';
import { DataModel } from '@blazemeter/blaze-data-model';
import { loadDataModelForService, updateDataModel } from '../../store/dataModel/dataModelActions';
import { setDataModelParamCount } from '../../store/dataModel/dataModelReducer';
import {
  getAccountPlan,
  isFindAndReserveIntegrationFeatureEnabled,
  isTdmRefreshBlazeDataModel,
  isTestDataOpenAiEnabled,
} from '../../utils/bzmUserConfigUtil';
import { showTDMMessage, TDMMessageType } from './TDMCommon';
import axios from 'axios/axios-base';
import { fetchServices } from '../../store/services/servicesActions';

interface DataDefinitionPanelContextAdapterProps {
  workspaceId: number;
  accountAiConsent?: boolean;
  aiConsent?: boolean;
  isPanelVisible: boolean;
  onClose(): void;
  selectedServiceId: number | null;
  services: Service[];
  accountId: number;
  useExpressionEditor: boolean;
  dataModel: DataModelState;
  isReadOnly?: boolean;
  children?: React.ReactNode;
  transactions?: Transaction[];
  onModelChangedCallback?: () => void;
}

const DataDefinitionPanelContextAdapter: FC<DataDefinitionPanelContextAdapterProps> = props => {
  const dispatch = useDispatch();

  const onModelChanged = (model: DataModel) => {
    dispatch(updateDataModel(model));
    props.onModelChangedCallback?.();
  };

  const updateBlazeDataParamCount = (count: number) => {
    dispatch(setDataModelParamCount(count));
  };

  const forceReloadDataModel = () => {
    dispatch(loadDataModelForService(props.selectedServiceId, true, false));
    dispatch(fetchServices(true));
  };

  const panelFeatures: PanelFeatures = {
    enableFindAndReserve: isFindAndReserveIntegrationFeatureEnabled(),
    useExpressionEditor: false,
    allowCSVFromTestConfig: false,
    disableSharedCsvDelete: true,
    disableSharedFolders: false,
    isReadOnly: !_.isNil(props.isReadOnly) ? props.isReadOnly : false,
    showOpenAI: isTestDataOpenAiEnabled(),
    enableTdmRefreshDataModel: isTdmRefreshBlazeDataModel(),
  };

  const getLastModifiedForService = async () => {
    const url = `/workspaces/{workspaceId}/transactions?serviceId=${props.selectedServiceId}&sort=-updated&limit=1&skip=0`;
    const response = await axios.get(url);
    if (response.data && response.data.result && response.data.result[0]) {
      const lastModified = response.data.result[0].updated;
      return lastModified as number;
    }
    return undefined;
  };

  useEffect(() => {
    if (props.isPanelVisible && props.selectedServiceId) {
      dispatch(loadDataModelForService(props.selectedServiceId, true, false));
    }
  }, [props.isPanelVisible, props.selectedServiceId]);

  const [activeService, setActiveService] = useState<ActiveServiceFile>();

  useEffect(() => {
    const service = props.services.find(s => {
      return s.id === props.selectedServiceId;
    });
    if (!service) {
      return;
    }
    (async () => {
      const lastModified = await getLastModifiedForService();
      const url = `${window.location.origin}/api/v1/workspaces/${props.workspaceId}/transactions?serviceId=${service.id}&filter=&sort=-id&type=HTTP&exportAsFile=true`;
      const name = service.name;
      setActiveService({
        name,
        url,
        lastModified,
        serviceId: service.id,
      });
    })();
  }, [props.selectedServiceId, JSON.stringify(props.transactions)]);

  const onShowMessage = (type: TDMMessageType, message: string, delay?: number) => {
    showTDMMessage(type, message, dispatch, delay);
  };

  const getContext = () => {
    // Hide context when panel is not visible, so when it is enabled again, context is mounted and re-initialized (DE22618)
    if (
      props.isPanelVisible !== undefined &&
      props.isPanelVisible &&
      !props.dataModel.isLoading &&
      !!props.dataModel.dataModel
    ) {
      return (
        <DataDefinitionContext
          dataModel={_.cloneDeep(props.dataModel.dataModel)}
          onModelChanged={onModelChanged}
          updateBlazeDataParamCount={updateBlazeDataParamCount}
          bzmWorkspaceId={props.workspaceId}
          accountId={props.accountId}
          reloadDataModel={false}
          arFetch={assetRepoFetch}
          ddFetch={dataDesignerFetch}
          dpFetch={dataDesignerFetchDP}
          onShowMessage={onShowMessage}
          panelFeatures={panelFeatures}
          setDDPanelVisible={props.onClose}
          contextCallbacks={contextCallbacksMockServices}
          activeService={activeService}
          forceReloadDataModel={forceReloadDataModel}
          aiConsent={props.aiConsent}
          plan={getAccountPlan()}
        >
          {props.children}
        </DataDefinitionContext>
      );
    }
    return props.children;
  };

  return <>{getContext()}</>;
};

export default DataDefinitionPanelContextAdapter;
